// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — create or edit an administration protocol from a drug's Protocols section.
//
// Editing never changes a protocol in place: it saves a NEW VERSION, and every drug in this pharmacy that uses
// the protocol moves to it (a global seed becomes this pharmacy's own copy). Before that happens the admin sees
// every affected drug by name and can instead create a new protocol for THIS drug only (ruling 2, Maya M1).

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppDialog from '../../../components/ui/AppDialog'
import AppButton from '../../../components/ui/AppButton'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import Notice from '../../../components/ui/Notice'
import { AppTextField, SelectField } from '../../../components/ui/FormFields'
import { useToast } from '../../../components/ui/Toast'
import { ApiError, type AdminProtocol, type AdminProtocolComponent } from '../../../api/client'
import { friendlyError } from '../../../api/errors'
import {
  SLOT_CATEGORY, SLOT_PRINTS_TO, toInput, type ProtocolInput, type ProtocolSlot,
} from '../../../api/carerxProtocols'
import { useCreateProtocol, useEditProtocol, useProtocolUsage } from '../../../hooks/queries/carerxProtocols'
import ProtocolLinesEditor from './ProtocolLinesEditor'
import ProtocolImpact from './ProtocolImpact'

export const SLOT_LABEL: Record<ProtocolSlot, string> = {
  premed: 'Premedication', kit: 'Anaphylaxis kit', flush: 'Flushing',
}

const PRINTS_TO = [
  { value: '', label: '— not printed —' },
  { value: 'enrollment_form_05', label: 'Enrollment form §05 (premedication)' },
  { value: 'enrollment_form_06', label: 'Enrollment form §06 (flushing)' },
  { value: 'enrollment_form_07', label: 'Enrollment form §07 (anaphylaxis kit)' },
]

/** Client-side check before the server's: name present, every line complete. Returns a message or null. */
export function problemOf(f: ProtocolInput): string | null {
  if (!f.name.trim()) return 'Give the protocol a name.'
  if (f.timing_minutes_prior !== null && (f.timing_minutes_prior < 0 || f.timing_minutes_prior > 1440))
    return 'Timing before infusion must be 0 to 1440 minutes.'
  const bad = f.lines.findIndex(l => (l.component_id !== null ? !l.component_id : !(l.line_text ?? '').trim()))
  return bad >= 0 ? `Line ${bad + 1} needs a component or text.` : null
}

/** The body the server takes: free-text lines trimmed, empty optionals as null. */
function clean(f: ProtocolInput): ProtocolInput {
  return {
    ...f,
    name: f.name.trim(),
    note: f.note?.trim() || null,
    prints_to: f.prints_to || null,
    lines: f.lines.map(l => ({ ...l, line_text: l.component_id !== null ? null : (l.line_text ?? '').trim() })),
  }
}

export interface SavedProtocol { id: string; version: number; scope: 'all' | 'this_drug' }

export default function ProtocolEditorDialog({ open, slot, protocol, components, drugReferenceId, onClose, onSaved }: {
  open: boolean
  slot: ProtocolSlot
  /** null = create a new protocol; otherwise edit this (current) version. */
  protocol: AdminProtocol | null
  components: AdminProtocolComponent[]
  /** The drug the dialog was opened from; a new protocol is assigned to it. */
  drugReferenceId: string | null
  onClose: () => void
  onSaved: (saved: SavedProtocol) => void
}) {
  const toast = useToast()
  const create = useCreateProtocol()
  const edit = useEditProtocol()
  const blank = (): ProtocolInput => ({
    name: '', category: SLOT_CATEGORY[slot], timing_minutes_prior: null, prints_to: SLOT_PRINTS_TO[slot], note: null, lines: [],
  })
  // Mounted fresh for each open (the caller renders it only while open), so the form starts from the
  // protocol once and a library refetch never wipes what the admin is typing.
  const [form, setForm] = useState<ProtocolInput>(() => (protocol ? toInput(protocol, components) : blank()))
  const [confirming, setConfirming] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const usage = useProtocolUsage(confirming && protocol ? protocol.id : null)

  const busy = create.isPending || edit.isPending
  const fail = (e: unknown) => {
    const conflict = e instanceof ApiError && e.status === 409
    const msg = conflict
      ? 'Someone saved a newer version of this protocol. Close this and open it again to see their change.'
      : friendlyError(e, 'The protocol could not be saved.')
    setErr(msg); toast.show(msg, 'error')
  }

  const createNew = (forThisDrug: boolean) =>
    create.mutate(
      { body: clean(form), assignDrugReferenceId: drugReferenceId ?? undefined, copiedFrom: forThisDrug ? protocol?.id : undefined },
      {
        onSuccess: r => {
          toast.show(forThisDrug ? 'New protocol created for this drug only.' : 'Protocol created.', 'success')
          setConfirming(false); onSaved({ id: r.id, version: r.version, scope: 'this_drug' })
        },
        onError: fail,
      })

  const saveForAll = () => {
    if (!protocol) return
    edit.mutate({ id: protocol.id, expectedVersion: protocol.version, body: clean(form) }, {
      onSuccess: r => {
        toast.show(`Saved as version ${r.version}. ${r.repointed_drugs} drug${r.repointed_drugs === 1 ? '' : 's'} moved to it.`, 'success')
        setConfirming(false); onSaved({ id: r.id, version: r.version, scope: 'all' })
      },
      onError: e => { setConfirming(false); fail(e) },
    })
  }

  const onSave = () => {
    const p = problemOf(form)
    if (p) { setErr(p); return }
    setErr(null)
    if (protocol) setConfirming(true)
    else createNew(false)
  }

  const drugs = usage.data?.drugs ?? []
  const names = drugs.map(d => d.name).join(', ')
  const title = protocol
    ? `Edit ${SLOT_LABEL[slot].toLowerCase()} protocol — ${protocol.name} (v${protocol.version}${protocol.scope === 'global' ? ', shared template' : ''})`
    : `New ${SLOT_LABEL[slot].toLowerCase()} protocol`

  return (
    <>
      <AppDialog open={open && !confirming} title={title} maxWidth="md" busy={busy} onClose={onClose}
        actions={<>
          <AppButton onClick={onClose} disabled={busy}>Cancel</AppButton>
          <AppButton variant="contained" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : protocol ? 'Save…' : 'Create protocol'}</AppButton>
        </>}>
        {err && <Notice severity="error">{err}</Notice>}
        {protocol?.scope === 'global' && (
          <Notice severity="info">This is a shared template. Saving creates your pharmacy's own copy; the template itself does not change.</Notice>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AppTextField label="Name" required fullWidth value={form.name} inputProps={{ maxLength: 200 }}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Stack direction="row" spacing={2}>
            <AppTextField label="Category" value={SLOT_LABEL[slot]} disabled sx={{ width: 200 }} />
            <AppTextField label="Timing before infusion (min)" type="number" sx={{ width: 230 }}
              value={form.timing_minutes_prior ?? ''} inputProps={{ min: 0, max: 1440 }}
              onChange={e => setForm(f => ({ ...f, timing_minutes_prior: e.target.value === '' ? null : Number(e.target.value) }))} />
            <SelectField label="Prints to" value={form.prints_to ?? ''} options={PRINTS_TO} sx={{ flex: 1 }}
              onChange={v => setForm(f => ({ ...f, prints_to: v || null }))} />
          </Stack>
          <AppTextField label="Note" multiline minRows={2} fullWidth value={form.note ?? ''} inputProps={{ maxLength: 2000 }}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
          <Box sx={{ fontSize: 13, fontWeight: 700 }}>Lines</Box>
          <ProtocolLinesEditor category={form.category} lines={form.lines} components={components}
            onChange={lines => setForm(f => ({ ...f, lines }))} />
        </Stack>
      </AppDialog>

      <ConfirmDialog open={open && confirming} tone="danger" busy={busy} maxWidth="sm"
        title={`Save a new version of ${protocol?.name ?? 'this protocol'}?`}
        confirmLabel="Save for all" busyLabel="Saving…"
        confirmDisabled={usage.isLoading || usage.isError}
        error={usage.isError ? friendlyError(usage.error, 'Could not load the drugs that use this protocol.') : null}
        onCancel={() => setConfirming(false)} onConfirm={saveForAll}
        body={
          <Stack spacing={1.5} data-testid="protocol-usage-warning">
            {usage.isLoading && <Box>Checking everything this protocol reaches…</Box>}
            {usage.data && (
              <>
                <Box>
                  {drugs.length > 0
                    ? <>This protocol is used by <b>{names}</b>; new enrollments of {drugs.length === 1 ? 'that drug' : 'those drugs'} will use the new version. Patients already enrolled keep theirs.</>
                    : <>No drug in this pharmacy uses this protocol yet. Saving creates version {protocol ? protocol.version + 1 : 2}.</>}
                </Box>
                <ProtocolImpact usage={usage.data} action="edit" />
                <Box>If these changes are not acceptable, create a new protocol instead.</Box>
              </>
            )}
            {drugReferenceId && (
              <Box data-testid="new-protocol-instead">
                <AppButton variant="outlined" disabled={busy || !usage.data} onClick={() => createNew(true)}>
                  Create a new protocol for this drug instead
                </AppButton>
                <Box sx={{ fontSize: 12, color: 'var(--color-text-soft)', mt: 0.5 }}>
                  Only this drug moves to the new protocol; the others keep the current version.
                </Box>
              </Box>
            )}
          </Stack>
        } />
    </>
  )
}
